import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Check, Loader2, TriangleAlert } from 'lucide-react';
import { clsx } from 'clsx';

// A conta vem do motor do servidor, pelo alias do Vite. É de propósito: o
// número que muda enquanto se digita tem que sair do MESMO código que grava o
// fechamento. Reescrever a regra aqui seria a quarta implementação dela neste
// projeto — e a terceira, dentro do VBA, estava errada.
import { aplicarAjustes, LANCAMENTO_VAZIO } from '@engine/ajustes.ts';
import type { RegrasDeAjuste } from '@engine/ajustes.ts';

import { api } from '../lib/api.ts';
import { CARGOS, reais } from '../lib/formato.ts';
import type { LinhaResultado } from '../lib/tipos.ts';
import { Aviso, Etiqueta, Tabela } from './ui.tsx';

interface Lancado {
  diasTrabalhados: string;
  faltasJustificadas: string;
  faltasInjustificadas: string;
  ajusteManual: string;
  observacao: string;
}

function inicial(l: LinhaResultado): Lancado {
  return {
    diasTrabalhados: l.diasTrabalhados === null ? '' : String(l.diasTrabalhados),
    faltasJustificadas: l.faltasJustificadas === 0 ? '' : String(l.faltasJustificadas),
    faltasInjustificadas: l.faltasInjustificadas === 0 ? '' : String(l.faltasInjustificadas),
    ajusteManual: !l.ajusteManual ? '' : String(l.ajusteManual).replace('.', ','),
    observacao: l.ajusteManualMotivo ?? '',
  };
}

/** Aceita "50", "-50", "1.234,56" e devolve número. Vazio vira zero. */
const decimal = (v: string): number => {
  const limpo = v.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(limpo);
  return limpo === '' || limpo === '-' || !Number.isFinite(n) ? 0 : n;
};

const inteiro = (v: string): number => {
  const n = Number(v);
  return v.trim() === '' || !Number.isFinite(n) || n < 0 ? 0 : Math.trunc(n);
};


/**
 * Dias de presença, e o que a projeção fez com o volume.
 *
 * A contagem é automática — dias distintos em que a pessoa aparece na base do
 * mês — e não tem campo para digitar. Por isso a coluna precisa explicar o que
 * está mostrando: é o número que divide o volume dela antes da comparação do
 * balizador, e quem olha a prévia tem que conseguir checar.
 */
function Presenca({ linha }: { linha: LinhaResultado }) {
  const dias = linha.diasDePresenca;
  const total = linha.diasDeOperacao;

  if (dias === null || dias === undefined || total === null || total === undefined) {
    return <span className="text-slate-300">—</span>;
  }

  const completo = dias >= total;

  if (linha.balizAmostraInsuficiente === true) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-xs
                   font-medium text-amber-800"
        title={
          `Apareceu em ${dias} de ${total} dias — poucos dias para projetar com segurança. ` +
          'O balizador usou o volume bruto, sem projeção. Confira se esta pessoa deveria entrar ' +
          'nesta competência, ou ajuste o mínimo em Regras.'
        }
      >
        <TriangleAlert className="size-3" />
        {dias}/{total}
      </span>
    );
  }

  if (linha.balizProjetado === true) {
    const bruto = linha.balizVolumeBruto ?? 0;
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-xs
                   font-medium text-sky-800"
        title={
          `Apareceu em ${dias} de ${total} dias. Para o balizador não puni-la pelos dias ` +
          `ausentes, o volume foi projetado para o mês cheio: ${bruto} ÷ ${dias} × ${total} = ` +
          `${Math.round(linha.balizVolume ?? 0)}. É este número que foi comparado com a média da equipe.`
        }
      >
        {dias}/{total}
      </span>
    );
  }

  return (
    <span
      className={clsx('text-xs', completo ? 'text-slate-400' : 'text-slate-600')}
      title={
        completo
          ? `Presente nos ${total} dias em que a operação teve agendamento.`
          : `Apareceu em ${dias} de ${total} dias. A projeção do balizador está desligada nas regras desta competência, então o volume foi comparado como veio da base.`
      }
    >
      {dias}/{total}
    </span>
  );
}

/**
 * A prévia com faltas e dias editáveis.
 *
 * Digitar recalcula na hora, localmente; sair do campo grava. O valor não é
 * um palpite da tela: é o resultado do mesmo `aplicarAjustes` que o servidor
 * roda ao fechar o mês.
 */
export function TabelaComAjustes({
  competencia,
  linhas,
  regras,
  podeEditar,
  aoMudarTotal,
}: {
  competencia: string;
  linhas: LinhaResultado[];
  regras: RegrasDeAjuste;
  podeEditar: boolean;
  /** Informa o total já ajustado, para o resumo do topo acompanhar. */
  aoMudarTotal?: (total: number) => void;
}) {
  const [lancados, setLancados] = useState<Record<number, Lancado>>(() =>
    Object.fromEntries(
      linhas
        .filter((l) => l.funcionarioFeegowId !== null && l.funcionarioFeegowId !== undefined)
        .map((l) => [l.funcionarioFeegowId as number, inicial(l)]),
    ),
  );
  const [aberta, setAberta] = useState<number | null>(null);
  const [salvo, setSalvo] = useState<number | null>(null);

  const salvar = useMutation({
    mutationFn: (v: { linha: LinhaResultado; valores: Lancado }) =>
      api<{ ok: true }>('/ajustes', {
        metodo: 'PUT',
        corpo: {
          competencia,
          funcionarioId: v.linha.funcionarioFeegowId,
          nome: v.linha.nome,
          diasTrabalhados: v.valores.diasTrabalhados.trim() === '' ? null : Number(v.valores.diasTrabalhados),
          faltasJustificadas: inteiro(v.valores.faltasJustificadas),
          faltasInjustificadas: inteiro(v.valores.faltasInjustificadas),
          ajusteManual: decimal(v.valores.ajusteManual),
          observacao: v.valores.observacao.trim(),
        },
      }),
    // Gravar não recarrega a prévia de propósito. O valor na tela já está
    // correto — foi calculado pelo mesmo motor do servidor. Refazer a prévia a
    // cada campo custaria uma releitura dos milhares de agendamentos no
    // Redshift e ainda tiraria o foco de quem está digitando.
    onSuccess: (_r, v) => {
      setSalvo(v.linha.funcionarioFeegowId ?? null);
      setTimeout(() => setSalvo(null), 2000);
    },
  });

  /** Recalcula tudo localmente com o motor, sem ida ao servidor. */
  const calculadas = useMemo(
    () =>
      linhas.map((l) => {
        const id = l.funcionarioFeegowId;
        const v = id === null || id === undefined ? undefined : lancados[id];
        if (v === undefined) return { linha: l, total: l.totalFinal, etapas: l.etapasDosAjustes ?? [], avisos: [] as string[] };

        const r = aplicarAjustes(
          l.premiacaoAntesAjustes ?? l.totalFinal,
          {
            ...LANCAMENTO_VAZIO,
            diasTrabalhados: v.diasTrabalhados.trim() === '' ? null : Number(v.diasTrabalhados),
            faltasJustificadas: inteiro(v.faltasJustificadas),
            faltasInjustificadas: inteiro(v.faltasInjustificadas),
            ajusteManual: decimal(v.ajusteManual),
            ajusteManualMotivo: v.observacao.trim() === '' ? null : v.observacao.trim(),
            salario: l.salarioReferencia ?? null,
          },
          regras,
          competencia,
        );
        return { linha: l, total: r.valorFinal, etapas: r.etapas, avisos: r.avisos };
      }),
    [linhas, lancados, regras, competencia],
  );

  const total = calculadas.reduce((s, c) => s + c.total, 0);
  const avisos = calculadas.flatMap((c) => c.avisos.map((a) => `${c.linha.nome}: ${a}`));

  // O resumo do topo acompanha o total ajustado. A referência evita depender da
  // identidade da função, que muda a cada render do pai e criaria laço.
  const avisar = useRef(aoMudarTotal);
  avisar.current = aoMudarTotal;
  useEffect(() => {
    avisar.current?.(total);
  }, [total]);

  function alterar(id: number, campo: keyof Lancado, valor: string) {
    setLancados((atual) => ({
      ...atual,
      [id]: {
        ...(atual[id] ?? {
          diasTrabalhados: '',
          faltasJustificadas: '',
          faltasInjustificadas: '',
          ajusteManual: '',
          observacao: '',
        }),
        [campo]: valor,
      },
    }));
  }

  /** Grava ao sair do campo, e só quando algo mudou de fato. */
  function gravar(id: number) {
    const linha = linhas.find((l) => l.funcionarioFeegowId === id);
    const valores = lancados[id];
    if (linha === undefined || valores === undefined) return;

    const original = inicial(linha);
    const mudou =
      original.diasTrabalhados !== valores.diasTrabalhados ||
      original.faltasJustificadas !== valores.faltasJustificadas ||
      original.faltasInjustificadas !== valores.faltasInjustificadas ||
      original.ajusteManual !== valores.ajusteManual ||
      original.observacao !== valores.observacao;

    // Ajuste sem motivo o servidor recusa, e com razão. Não adianta mandar
    // para tomar 400 e perder o que a pessoa digitou: espera o motivo.
    if (decimal(valores.ajusteManual) !== 0 && valores.observacao.trim() === '') return;

    if (mudou) salvar.mutate({ linha, valores });
  }

  const entrada = (id: number, campo: keyof Lancado, placeholder: string) => (
    <input
      value={lancados[id]?.[campo] ?? ''}
      placeholder={placeholder}
      disabled={!podeEditar}
      inputMode="numeric"
      onChange={(e) => alterar(id, campo, e.target.value.replace(/[^\d]/g, ''))}
      onBlur={() => gravar(id)}
      className="w-16 rounded border border-slate-300 px-2 py-1 text-right font-mono text-sm
                 focus:border-slate-900 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400"
    />
  );

  return (
    <div className="space-y-4">
      {avisos.length > 0 && (
        <Aviso titulo="Lançamentos que não puderam ser aplicados">
          {avisos.map((a) => (
            <p key={a}>· {a}</p>
          ))}
        </Aviso>
      )}

      {salvar.isError && (
        <Aviso tom="erro">
          <p>{salvar.error.message}</p>
        </Aviso>
      )}

      <Tabela
        cabecalho={
          <tr>
            <th className="py-2 pr-3">Nome</th>
            <th className="py-2 pr-3">Cargo</th>
            <th className="py-2 pr-3 text-center">Presença</th>
            <th className="py-2 pr-3 text-right">Premiação</th>
            <th className="py-2 pr-3 text-center">Dias</th>
            <th className="py-2 pr-3 text-center">Faltas just.</th>
            <th className="py-2 pr-3 text-center">Faltas não just.</th>
            <th className="py-2 pr-3 text-center">Ajuste R$</th>
            <th className="py-2 pr-3 text-right">Total</th>
            <th className="py-2" />
          </tr>
        }
      >
        {calculadas.map(({ linha: l, total: t, etapas }) => {
          const id = l.funcionarioFeegowId;
          const antes = l.premiacaoAntesAjustes ?? l.totalFinal;
          const mudou = Math.abs(t - antes) > 0.004;

          return (
            <Fragment key={id ?? l.nome}>
              <tr className="hover:bg-slate-50">
                <td className="py-2 pr-3">
                  {etapas.length > 0 ? (
                    <button
                      onClick={() => setAberta(aberta === id ? null : (id ?? null))}
                      className="inline-flex items-center gap-1 text-left hover:underline"
                    >
                      {aberta === id ? (
                        <ChevronDown className="size-3.5 shrink-0" />
                      ) : (
                        <ChevronRight className="size-3.5 shrink-0" />
                      )}
                      {l.nome}
                    </button>
                  ) : (
                    <span className="pl-4.5">{l.nome}</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-slate-600">{CARGOS[l.cargoCodigo] ?? l.cargoCodigo}</td>
                <td className="py-2 pr-3 text-center">
                  <Presenca linha={l} />
                </td>
                <td className="num py-2 pr-3 text-slate-500">{reais(antes)}</td>
                <td className="py-2 pr-3 text-center">
                  {id === null || id === undefined ? '—' : entrada(id, 'diasTrabalhados', String(l.diasBase ?? ''))}
                </td>
                <td className="py-2 pr-3 text-center">
                  {id === null || id === undefined ? '—' : entrada(id, 'faltasJustificadas', '0')}
                </td>
                <td className="py-2 pr-3 text-center">
                  {id === null || id === undefined ? '—' : entrada(id, 'faltasInjustificadas', '0')}
                </td>
                <td className="py-2 pr-3 text-center">
                  {id === null || id === undefined ? (
                    '—'
                  ) : (
                    <input
                      value={lancados[id]?.ajusteManual ?? ''}
                      placeholder="0,00"
                      disabled={!podeEditar}
                      inputMode="decimal"
                      onChange={(e) =>
                        alterar(id, 'ajusteManual', e.target.value.replace(/[^\d,.-]/g, ''))
                      }
                      onBlur={() => gravar(id)}
                      className="w-24 rounded border border-slate-300 px-2 py-1 text-right font-mono text-sm
                                 focus:border-slate-900 focus:outline-none disabled:bg-slate-50
                                 disabled:text-slate-400"
                    />
                  )}
                </td>
                <td
                  className={clsx(
                    'num py-2 pr-3 font-semibold',
                    mudou && (t < antes ? 'text-red-700' : 'text-emerald-700'),
                  )}
                >
                  {reais(t)}
                </td>
                <td className="py-2 pr-2">
                  {salvar.isPending && salvar.variables?.linha.funcionarioFeegowId === id && (
                    <Loader2 className="size-4 animate-spin text-slate-400" />
                  )}
                  {salvo === id && <Check className="size-4 text-emerald-600" />}
                </td>
              </tr>

              {/* O motivo aparece quando há ajuste, numa linha própria: a
                  tabela já tem dez colunas, e um campo de texto espremido
                  convidaria a escrever "ajuste" e seguir em frente. */}
              {id !== null && id !== undefined && decimal(lancados[id]?.ajusteManual ?? '') !== 0 && (
                <tr className="bg-amber-50/40">
                  <td colSpan={10} className="px-3 pb-3">
                    <label className="flex items-center gap-2">
                      <span className="shrink-0 text-xs font-medium text-amber-900">
                        Motivo do ajuste de {reais(decimal(lancados[id]?.ajusteManual ?? ''))}
                      </span>
                      <input
                        value={lancados[id]?.observacao ?? ''}
                        placeholder="Obrigatório — o que aconteceu para o valor ser outro"
                        disabled={!podeEditar}
                        onChange={(e) => alterar(id, 'observacao', e.target.value)}
                        onBlur={() => gravar(id)}
                        className="flex-1 rounded border border-amber-300 bg-white px-2 py-1 text-sm
                                   focus:border-amber-600 focus:outline-none disabled:bg-slate-50"
                      />
                    </label>
                    {(lancados[id]?.observacao ?? '').trim() === '' && (
                      <p className="mt-1 text-xs text-amber-800">
                        Enquanto o motivo estiver em branco, o ajuste não é gravado. Um valor que
                        ninguém sabe explicar é o que o comprovante não consegue justificar depois.
                      </p>
                    )}
                  </td>
                </tr>
              )}

              {aberta === id && etapas.length > 0 && (
                <tr className="bg-slate-50">
                  <td colSpan={10} className="px-3 py-3">
                    <p className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">
                      Como os ajustes mudaram o valor
                    </p>
                    <ol className="space-y-1 text-sm text-slate-700">
                      {etapas.map((e, i) => (
                        <li key={i}>
                          · {e.descricao} <span className="text-slate-400">→</span>{' '}
                          <strong className="font-mono">{reais(e.valorDepois)}</strong>
                        </li>
                      ))}
                    </ol>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}

        <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
          <td className="py-3 pr-3" colSpan={8}>
            Total geral
          </td>
          <td className="num py-3 pr-3">{reais(total)}</td>
          <td />
        </tr>
      </Tabela>

      <p className="text-xs text-slate-500">
        Deixe os dias em branco para mês cheio. O valor muda enquanto você digita e é gravado ao sair
        do campo — os mesmos números que o fechamento vai usar.{' '}
        <span className="block pt-1">
          A coluna <strong>Presença</strong> é contada sozinha: em quantos dias a pessoa aparece na
          base, sobre os dias em que a operação teve agendamento. Não se digita, e passar o mouse
          mostra o que ela fez com o volume do balizador.
        </span>{' '}
        {!podeEditar && <Etiqueta tom="neutro">seu perfil não permite lançar</Etiqueta>}
      </p>
    </div>
  );
}
